<template>
  <TransitionRoot as="template" :show="bandwidthStore.isModalOpen">
    <Dialog as="div" class="fixed z-20 inset-0 overflow-y-auto" @close="bandwidthStore.closeModal">
      <div
        class="
          flex
          items-end
          justify-center
          min-h-screen
          pt-4
          px-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              relative
              inline-block
              align-bottom
              bg-vice-light-grey
              rounded-lg
              p-6
              text-left
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-lg
              sm:w-full
              sm:p-6
            "
          >
            <div class="sm:flex flex-col sm:items-start w-full">
              <!-- Header -->
              <h2 class="w-full font-bold text-center mb-1 text-lg">Bandwidth Control</h2>
              <p class="w-full text-center text-sm text-gray-600 mb-4">
                Disable inbound streams to reduce bandwidth usage
              </p>

              <!-- Stream List -->
              <div v-if="availableStreams && availableStreams.length > 0" class="w-full space-y-3">
                <div
                  v-for="stream in availableStreams"
                  :key="stream.jitsiUserId"
                  class="
                    flex
                    items-center
                    justify-between
                    p-3
                    bg-white
                    rounded-lg
                    shadow-sm
                    transition-shadow
                  "
                  :class="!stream.switchable ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md'"
                >
                  <!-- Stream Info -->
                  <div class="flex items-center space-x-3 flex-1">
                    <!-- Icon -->
                    <component
                      :is="getIconComponent(stream.icon)"
                      class="w-12 h-12 flex-shrink-0 text-vice-teal-700"
                      :class="{ 'opacity-60': !isStreamEnabled(stream.jitsiUserId) }"
                      pathFill="#0F766E"
                    />

                    <!-- Details -->
                    <div class="flex flex-col flex-1">
                      <span class="font-medium text-sm">
                        {{ stream.label }}
                        <span v-if="!stream.switchable" class="text-xs text-gray-500">
                          (Cannot be turned off)
                        </span>
                      </span>
                      <span class="text-xxs text-gray-700">{{ stream.estimatedBandwidth }}</span>
                    </div>

                    <!-- Active Indicator -->
                    <div v-if="stream.isActive" class="flex items-center space-x-1">
                      <div class="bg-green-500 w-2 h-2 rounded-full"></div>
                      <span class="text-gray-600">Active</span>
                    </div>
                  </div>

                  <!-- Toggle Switch -->
                  <Toggle
                    v-if="stream.switchable"
                    :enabled="isStreamEnabled(stream.jitsiUserId)"
                    @click="toggleStream(stream.jitsiUserId)"
                    class="ml-3"
                  />
                </div>
              </div>

              <!-- No Streams Message -->
              <div v-else class="w-full text-center text-gray-500 py-8">
                No clinician streams available
              </div>

              <!-- Bandwidth Summary -->
              <div
                v-if="availableStreams && availableStreams.length > 0"
                class="w-full mt-4 pt-4 border-t border-gray-300 flex justify-between text-sm"
              >
                <div>
                  <span class="font-medium">Estimated bandwidth:</span>
                  <span class="ml-2 text-vice-teal-700 font-bold">{{ totalBandwidth }} Mbps</span>
                </div>
                <div>
                  <span class="font-medium">Disabled streams:</span>
                  <span class="ml-2 text-gray-600">{{ disabledCount }}</span>
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import Toggle from '@/Components/Inputs/Toggle.vue';
import ViceCamera from '@/Components/Icons/ViceCamera.vue';
import ViceGlasses from '@/Components/Icons/ViceGlasses.vue';
import ViceMonitor from '@/Components/Icons/ViceMonitor.vue';
import ViceExamCam from '@/Components/Icons/ViceExamCam.vue';
import ViceOtoscope from '@/Components/Icons/ViceOtoscope.vue';
import MainCameraIcon from '@/Components/Icons/MainCameraIcon.vue';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';
import ViceDermatoscope from '@/Components/Icons/ViceDermatoscope.vue';

import useSessionStore from '@/Stores/useSessionStore';
import useBandwidthControlStore from '@/Stores/useBandwidthControlStore';

export default {
  name: 'BandwidthControlModal',
  components: {
    Dialog,
    Toggle,
    ViceCamera,
    ViceMonitor,
    ViceGlasses,
    ViceExamCam,
    ViceOtoscope,
    DialogOverlay,
    MainCameraIcon,
    TransitionRoot,
    TransitionChild,
    ViceStethoscope,
    ViceDermatoscope,
  },
  props: {
    availableStreams: {
      type: Array,
      default: () => [],
    },
    totalBandwidth: {
      type: [String, Number],
      default: '0',
    },
    disabledCount: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    const sessionStore = useSessionStore();
    const bandwidthStore = useBandwidthControlStore();

    const isStreamEnabled = (jitsiUserId) =>
      bandwidthStore.isStreamEnabled(sessionStore.sessionId, jitsiUserId);

    const toggleStream = (jitsiUserId) => {
      bandwidthStore.toggleStream(sessionStore.sessionId, jitsiUserId);
    };

    const getIconComponent = (iconName) => {
      const iconMap = {
        ViceStethoscope,
        ViceDermatoscope,
        ViceExamCam,
        ViceMonitor,
        ViceCamera,
        ViceOtoscope,
        ViceGlasses,
        MainCameraIcon,
      };
      return iconMap[iconName];
    };

    return {
      bandwidthStore,
      isStreamEnabled,
      toggleStream,
      getIconComponent,
    };
  },
};
</script>
